import { useState } from "react";
import { Check, Loader2, Mail, PauseCircle } from "lucide-react";

import { ApiError, api } from "../api/client";

interface EmailResult {
  sent: boolean;
  email_paused: boolean;
  sent_to: string;
  message: string;
  last_emailed_at: string | null;
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

/**
 * Admin action: email the employee that this document is waiting for them
 * (POST /api/documents/{id}/email). For a document that still needs signing
 * the email asks them to sign it; once they sign, the uploader and their
 * line manager are emailed back.
 *
 * While all email is paused the server sends only to the test recipients set
 * in Settings > Email and says so in its reply, which is shown in place.
 */
export default function EmailDocumentButton({
  documentId,
  needsSignature,
  lastEmailedAt,
  onSent,
  compact = false,
}: {
  documentId: number;
  needsSignature: boolean;
  lastEmailedAt?: string | null;
  onSent?: () => void;
  compact?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<EmailResult | null>(null);
  const [error, setError] = useState("");
  const [emailedAt, setEmailedAt] = useState<string | null>(lastEmailedAt ?? null);

  const size = compact ? "px-2 py-1" : "px-3 py-1.5";
  const label = needsSignature ? "Email for signature" : "Email employee";

  const send = async () => {
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const res = await api.post<EmailResult>(`/api/documents/${documentId}/email`, {});
      setResult(res);
      if (res.last_emailed_at) setEmailedAt(res.last_emailed_at);
      if (res.sent) onSent?.();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't send the email");
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <button
        type="button"
        onClick={() => void send()}
        disabled={busy}
        className={`inline-flex items-center gap-1 rounded-lg border border-pme-blue/30 ${size} text-xs font-medium text-pme-blue hover:bg-pme-blue/5 disabled:opacity-60`}
        title={needsSignature ? "Email the employee asking them to sign this document" : "Email the employee about this document"}
      >
        {busy ? <Loader2 size={12} className="animate-spin" aria-hidden /> : <Mail size={12} aria-hidden />}
        {label}
      </button>
      {emailedAt && !result && (
        <span className="text-[11px] text-gray-500">Emailed {shortDate(emailedAt)}</span>
      )}
      {result && result.sent && (
        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-green-700">
          <Check size={12} aria-hidden /> {result.message}
        </span>
      )}
      {result && !result.sent && (
        <span className="inline-flex basis-full items-start gap-1 text-[11px] text-amber-800">
          <PauseCircle size={12} className="mt-0.5 shrink-0" aria-hidden /> {result.message}
        </span>
      )}
      {error && <span className="basis-full text-[11px] text-red-700">{error}</span>}
    </span>
  );
}
